import { useState, useEffect } from 'react'
import { ENDPOINTS } from '@/config/endpoints'

interface SimilarItem {
  type: 'flash' | 'portfolioItem' | 'healedPortfolioItem'
  id: string
}

interface SimilarItemsResponse {
  left: SimilarItem[]
  right: SimilarItem[]
}

interface UseSimilarItemsReturn {
  leftItems: SimilarItem[]
  rightItems: SimilarItem[]
  loading: boolean
  error: string | null
}

export function useSimilarItems(
  itemId: string | null, 
  itemType: 'flash' | 'portfolioItem' | 'healedPortfolioItem'
): UseSimilarItemsReturn {
  const [leftItems, setLeftItems] = useState<SimilarItem[]>([])
  const [rightItems, setRightItems] = useState<SimilarItem[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!itemId) return

    const fetchSimilarItems = async () => {
      setLoading(true)
      setError(null)

      try {
        const response = await fetch(ENDPOINTS.get_similar_items, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: itemId,
            type: itemType,
          }),
        })

        if (!response.ok) {
          throw new Error(`Failed to fetch similar items: ${response.statusText}`)
        }

        const data: SimilarItemsResponse = await response.json()
        setLeftItems(data.left || [])
        setRightItems(data.right || [])
      } catch (err) {
        console.error('Error fetching similar items:', err)
        setError(err instanceof Error ? err.message : 'Unknown error')
      } finally {
        setLoading(false)
      }
    }

    fetchSimilarItems()
  }, [itemId, itemType])

  return { leftItems, rightItems, loading, error }
}

